import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import type { BreadcrumbItem } from '@/components/Breadcrumb';
import { ConversationMessage } from '@/components/ConversationMessage';
import { GradeBadge } from '@/components/GradeBadge';
import type { AdminConversationDetailResponse, ConversationDetailsResponse } from '@/types/admin';
import type { User } from '@/types/auth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './AdminConversation.css';
import '../styles/common.css';
import '../styles/chat-messages.css';
import '../styles/markdown.css';

interface EditingScore {
  sequenceNumber: number;
  value: string;
}

const ScoreBadge: React.FC<{
  message: ConversationDetailsResponse;
  conversationId: string;
  locked: boolean;
  onScoreUpdated: (sequenceNumber: number, newScore: number, newGrade?: number) => void;
}> = ({ message, conversationId, locked, onScoreUpdated }) => {
  const [editing, setEditing] = useState<EditingScore | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (locked || message.scorePercentage === undefined) return;
    setEditing({
      sequenceNumber: message.sequenceNumber,
      value: Math.round(message.scorePercentage).toString(),
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    const newScore = Math.max(0, Math.min(100, Math.round(Number(editing.value) || 0)));
    setEditing(null);

    if (newScore === Math.round(message.scorePercentage ?? 0)) return;

    try {
      const result = await adminService.updateMessageScore({
        id: conversationId,
        sequenceNumber: editing.sequenceNumber,
        scorePercentage: newScore,
      });
      onScoreUpdated(editing.sequenceNumber, newScore, result.grade ?? undefined);
    } catch (err) {
      // Fehler wird stillschweigend ignoriert
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditing(null);
    }
  };

  if (message.scorePercentage === undefined) return null;

  if (editing) {
    return (
      <div className="chat-score-badge-top-right chat-score-editing">
        <input
          ref={inputRef}
          type="number"
          min={0}
          max={100}
          className="score-inline-input"
          value={editing.value}
          onChange={e => setEditing({ ...editing, value: e.target.value })}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
        />
        <span className="score-percent-sign">%</span>
      </div>
    );
  }

  const showKiScore =
    message.kiScorePercentage !== undefined &&
    Math.round(message.kiScorePercentage) !== Math.round(message.scorePercentage);

  const baseTitle = showKiScore ? `KI: ${Math.round(message.kiScorePercentage!)}%` : '';
  const title = locked
    ? (baseTitle ? `${baseTitle} — ` : '') + 'Punkte als angepasst bestätigt'
    : (baseTitle ? `${baseTitle} — Click to edit` : 'Click to edit');

  return (
    <div
      className={`chat-score-badge-top-right${locked ? '' : ' chat-score-clickable'}`}
      onClick={handleStartEdit}
      title={title}
    >
      {Math.round(message.scorePercentage)}%
      {showKiScore && (
        <span className="ki-score-hint">KI: {Math.round(message.kiScorePercentage!)}%</span>
      )}
    </div>
  );
};

const AdminConversation: React.FC = () => {
  const [conversation, setConversation] = useState<AdminConversationDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const conversationId = searchParams.get('conversationId');
  const adminModuleId = sessionStorage.getItem('adminModuleId');
  const studentName = [sessionStorage.getItem('adminUserFirstName'), sessionStorage.getItem('adminUserLastName')].filter(Boolean).join(' ');

  const [backModuleName, setBackModuleName] = useState<string>('');
  const [backClassName, setBackClassName] = useState<string>('');

  const fetchBackNames = useCallback(async () => {
    const backTo = sessionStorage.getItem('adminBackTo');
    if (backTo === 'days') {
      const backClassId = sessionStorage.getItem('adminBackClassId') ?? '';
      const [mName, cName] = await Promise.all([
        adminModuleId ? adminService.getModuleNameById(adminModuleId) : Promise.resolve(''),
        backClassId ? adminService.getClassNameById(backClassId) : Promise.resolve(''),
      ]);
      setBackModuleName(mName);
      setBackClassName(cName);
    } else if (backTo === 'user-conversations') {
      const backClassId = sessionStorage.getItem('adminBackClassId') ?? '';
      if (backClassId) {
        const cName = await adminService.getClassNameById(backClassId);
        setBackClassName(cName);
      }
    }
  }, [adminModuleId]);

  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const backTo = sessionStorage.getItem('adminBackTo');
    const items: BreadcrumbItem[] = [{ label: 'Modules', path: '/admin/modules' }];

    if (backTo === 'days') {
      const backDate = sessionStorage.getItem('adminBackDate') ?? '';
      const backClassId = sessionStorage.getItem('adminBackClassId') ?? '';
      const backPageTitle = [backModuleName, backClassName].filter(Boolean).join(' - ');
      const daysQuery = new URLSearchParams({ moduleId: adminModuleId || '', classId: backClassId });
      items.push({ label: backPageTitle ? `${backPageTitle} - Days` : 'Days', path: `/admin/days?${daysQuery}` });
      const dayConvQuery = new URLSearchParams({ moduleId: adminModuleId || '', date: backDate, classId: backClassId });
      items.push({ label: `Conversations from ${backDate}`, path: `/admin/day-conversations?${dayConvQuery}` });
    } else if (backTo === 'user-conversations') {
      const backUserId = sessionStorage.getItem('adminBackUserId') ?? '';
      const backUserFirstName = sessionStorage.getItem('adminBackUserFirstName') ?? '';
      const backUserLastName = sessionStorage.getItem('adminBackUserLastName') ?? '';
      const backClassId = sessionStorage.getItem('adminBackClassId') ?? '';
      const classQuery = new URLSearchParams();
      if (backClassId) classQuery.set('classId', backClassId);
      items.push({ label: backClassName ? `Users ${backClassName}` : 'Users', path: `/admin/class-users?${classQuery}` });
      const userConvQuery = new URLSearchParams({ userId: backUserId });
      if (backClassId) userConvQuery.set('classId', backClassId);
      const backUserName = [backUserFirstName, backUserLastName].filter(Boolean).join(' ');
      items.push({ label: `Conversations by ${backUserName}`, path: `/admin/user-conversations?${userConvQuery}` });
    }

    items.push({ label: 'Conversation' });
    return items;
  }, [adminModuleId, backModuleName, backClassName]);

  useEffect(() => {
    fetchUserInfo();
    fetchBackNames();
    if (conversationId) {
      fetchConversation();
    } else {
      setError('No conversation ID provided');
      setLoading(false);
    }
  }, [conversationId, fetchBackNames]);

  const fetchUserInfo = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const fetchConversation = async () => {
    if (!conversationId) return;
    
    try {
      setLoading(true);
      setError(null);
      const conversationData = await adminService.getConversationById(conversationId);
      setConversation(conversationData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    const backTo = sessionStorage.getItem('adminBackTo');
    const backDate = sessionStorage.getItem('adminBackDate');
    if (backTo === 'days' && adminModuleId && backDate) {
      const backClassId = sessionStorage.getItem('adminBackClassId');
      const query = new URLSearchParams({ moduleId: adminModuleId, date: backDate, classId: backClassId || '' });
      navigate(`/admin/day-conversations?${query}`);
    } else if (backTo === 'user-conversations') {
      const backUserId = sessionStorage.getItem('adminBackUserId');
      const backClassId = sessionStorage.getItem('adminBackClassId');
      const query = new URLSearchParams({ userId: backUserId || '' });
      if (backClassId) query.set('classId', backClassId);
      navigate(`/admin/user-conversations?${query}`);
    } else if (adminModuleId) {
      navigate(`/admin/users?moduleId=${adminModuleId}`);
    } else {
      navigate('/admin/modules');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).replace('.', '');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}. ${month} ${year} ${hours}:${minutes}`;
  };

  const handleReopenConversation = async () => {
    if (!conversationId) return;

    const confirmed = window.confirm('Do you really want to reopen this conversation? The evaluation will be reset.');
    if (!confirmed) return;

    try {
      setError(null);
      await adminService.reopenConversation(conversationId);
      await fetchConversation();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reopen conversation');
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationId) return;

    const confirmed = window.confirm('Do you really want to delete this conversation? This action cannot be undone.');
    if (!confirmed) return;

    try {
      setError(null);
      await adminService.deleteConversation(conversationId);
      handleBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  };

  const handleScoreUpdated = (sequenceNumber: number, newScore: number, newGrade?: number) => {
    setConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        grade: newGrade !== undefined ? newGrade : prev.grade,
        messages: prev.messages.map(msg =>
          msg.sequenceNumber === sequenceNumber
            ? { ...msg, scorePercentage: newScore, kiScorePercentage: msg.kiScorePercentage ?? msg.scorePercentage }
            : msg
        ),
      };
    });
  };

  const handleToggleDiscussed = async () => {
    if (!conversationId) return;

    try {
      setError(null);
      await adminService.toggleDiscussed(conversationId);
      await fetchConversation();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle discussed status');
    }
  };

  const handleToggleAdjusted = async () => {
    if (!conversationId) return;

    try {
      setError(null);
      await adminService.toggleAdjusted(conversationId);
      await fetchConversation();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle adjusted status');
    }
  };

  return (
    <div className="admin-conversation-container">
      <AppHeader 
        user={user} 
        title="KI Expert - Admin" 
        subtitle="Conversation details" 
      />
      
      <main className="admin-conversation-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading conversation...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error loading</h3>
            <p>{error}</p>
            <button onClick={fetchConversation} className="btn btn-primary">
              Try again
            </button>
          </div>
        ) : conversation ? (
          <div className="conversation-detail">
            <div className={`conversation-header${conversation.isDiscussed ? ' discussed' : ''}${conversation.isAdjusted ? ' adjusted' : ''}${conversation.isAborted ? ' aborted' : ''}`}>
              <div className="conversation-info">
                <h2>{conversation.subjectNumber} - {conversation.subjectTitle}</h2>
                <div className="conversation-status-badges">
                  {conversation.isAborted && (
                    <span className="status-badge aborted-badge">Aborted</span>
                  )}
                  <GradeBadge grade={conversation.grade} />
                </div>
              </div>
              <div className="conversation-dates">
                {studentName && <span><strong>Student:</strong> {studentName}</span>}
                <span><strong>Created:</strong> {formatDate(conversation.createdAt)}</span>
                <span><strong>ID:</strong> {conversation.id}</span>
              </div>
            </div>

            <div className="participants-info">
              <details className="admin-collapsible">
                <summary className="admin-collapsible-summary">Prompt to AI (module + subject)</summary>
                <div className="admin-collapsible-content">{conversation.generationPrompt}</div>
              </details>
            </div>

            <div className="messages-section">
              <div className="chat-messages-container">
                {conversation.messages.map((message) => (
                  <React.Fragment key={message.id}>
                    {/* Question - left aligned */}
                    {/* Answer - right aligned, student evaluation style */}
                    <ConversationMessage
                      question={message.question}
                      questionLabel={`Question ${message.sequenceNumber}/${conversation.messages.length}`}
                      answer={message.answer}
                      feedback={message.kiFeedback}
                      scorePercentage={message.scorePercentage}
                      showEvaluation
                      scoreBadge={(
                        <ScoreBadge
                          message={message}
                          conversationId={conversation.id}
                          locked={conversation.isAdjusted}
                          onScoreUpdated={handleScoreUpdated}
                        />
                      )}
                    />

                    {/* Subject instructions - collapsible */}
                    {message.subject && (
                      <details className="admin-meta-box admin-collapsible">
                        <summary className="admin-collapsible-summary">Instructions (subject)</summary>
                        <div className="admin-meta-text" style={{ whiteSpace: 'pre-wrap' }}>{message.subject.replace(/\\n/g, '\n')}</div>
                      </details>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Overall grade footer */}
            {conversation.grade != null && (
              <div className="conversation-grade-footer">
                <span className="conversation-grade-label">Overall result:</span>
                <GradeBadge grade={conversation.grade} />
              </div>
            )}

            <div className="conversation-actions">
              <button
                className={`btn ${conversation.isAdjusted ? 'btn-adjusted' : 'btn-not-adjusted'}`}
                onClick={handleToggleAdjusted}
              >
                {conversation.isAdjusted ? 'Mark as not adjusted' : 'Mark as adjusted'}
              </button>
              <button
                className={`btn ${conversation.isDiscussed ? 'btn-discussed' : 'btn-not-discussed'}`}
                onClick={handleToggleDiscussed}
              >
                {conversation.isDiscussed ? 'Mark as not discussed' : 'Mark as discussed'}
              </button>
              {conversation.isAborted && (
                <button className="btn btn-primary" onClick={handleReopenConversation}>Reopen</button>
              )}
              <button className="btn btn-danger" onClick={handleDeleteConversation}>Delete</button>
            </div>
          </div>
        ) : (
          <div className="empty-container">
            <div className="empty-icon">💬</div>
            <h3>No conversation found</h3>
            <p>The requested conversation could not be found.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminConversation;
